import { useId, useState } from 'react';
import { RESET_PATH } from '@zevik/schemas';
import { signIn } from '../../credential/api';
import { Refused } from './CredentialShell';

/**
 * The sign-in form, on its own page or above a workspace whose session ended
 * (docs/DECISIONS.md ZD-118 §3, §4).
 *
 * What it says on failure is what the API said and nothing more. `sds-13a`
 * returns one sentence for the three causes ZD-117 §7 names, with the timing
 * equalised, and the form neither infers which nor offers a different
 * sentence for a different status — a 501 saying this
 * deployment does not sign people in by password is shown in those words.
 */
export function SignInForm({
  onSignedIn,
  compact = false,
}: {
  onSignedIn: () => void | Promise<void>;
  compact?: boolean;
}): React.JSX.Element {
  const emailId = useId();
  const passwordId = useId();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sending, setSending] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setRefusal(null);
    setSending(true);
    try {
      const outcome = await signIn(email.trim(), password);
      if (!outcome.ok) {
        setRefusal(outcome.message);
        return;
      }
      setPassword('');
      await onSignedIn();
    } catch (error) {
      setRefusal(error instanceof Error ? error.message : String(error));
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      className={compact ? 'cred__form cred__form--compact' : 'cred__form'}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <div className="cred__field">
        <label className="field" htmlFor={emailId}>
          Email
        </label>
        <input
          id={emailId}
          type="email"
          value={email}
          autoComplete="username"
          autoFocus={!compact}
          required
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
      </div>
      <div className="cred__field">
        <label className="field" htmlFor={passwordId}>
          Password
        </label>
        <input
          id={passwordId}
          type="password"
          value={password}
          autoComplete="current-password"
          required
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />
      </div>

      {refusal === null ? null : <Refused message={refusal} />}

      <div className="cred__actions">
        <button type="submit" className="submit" disabled={sending}>
          {sending ? 'Signing in…' : 'Sign in'}
        </button>
        <a className="cred__aside" href={RESET_PATH}>
          Forgotten your password?
        </a>
      </div>
    </form>
  );
}
